import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarClock, Plus, Trash2 } from "lucide-react";
import { rhm, apiError, dt } from "../api";
import { Badge, Btn, Card, Empty, Field, Input, Modal, Select, Table, Td, Textarea } from "../ui";

const BLANK = {
    name: "", placement: "top_bar", headline: "", message: "", link_label: "", link_href: "",
    tone: "gold", image_url: "", start_at: "", end_at: "", is_active: true, dismissible: true, priority: 0,
};

const PLACEMENT_LABELS = {
    top_bar: "Site-wide top bar", homepage: "Homepage", shop: "Shop pages",
    blog: "Blog pages", consultation: "Consultation page",
};

const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const toIso = (local) => (local ? new Date(local).toISOString() : "");

const STATE_TONE = { live: "good", scheduled: "gold", expired: "muted", off: "danger" };

export const BannerManager = ({ canEdit }) => {
    const qc = useQueryClient();
    const [editing, setEditing] = useState(null);
    const { data } = useQuery({ queryKey: ["site-banners-crm"], queryFn: async () => (await rhm.get("/banners")).data });
    const banners = data?.banners || [];

    const save = async (form) => {
        try {
            const payload = { ...form, start_at: toIso(form.start_local), end_at: toIso(form.end_local),
                priority: Number(form.priority) || 0 };
            delete payload.start_local; delete payload.end_local; delete payload.state; delete payload.id;
            delete payload.created_at; delete payload.updated_at; delete payload.created_by; delete payload.updated_by;
            if (form.id) await rhm.put(`/banners/${form.id}`, payload);
            else await rhm.post("/banners", payload);
            toast.success(form.id ? "Banner updated" : "Banner created");
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["site-banners-crm"] });
        } catch (e) { toast.error(apiError(e)); }
    };

    const remove = async (b) => {
        if (!window.confirm(`Delete “${b.name}”?`)) return;
        try {
            await rhm.delete(`/banners/${b.id}`);
            toast.success("Banner deleted");
            qc.invalidateQueries({ queryKey: ["site-banners-crm"] });
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <Card title="Promo banners" data-testid="banner-manager"
            action={canEdit && (
                <Btn onClick={() => setEditing({ ...BLANK, start_local: "", end_local: "" })} data-testid="banner-new">
                    <Plus className="w-3.5 h-3.5 mr-1 inline" /> BANNER
                </Btn>
            )}>
            <p className="text-sm text-zinc-400 mb-4">
                Schedule a promotion once — it appears at the start date and disappears on its own at the end date.
                Leave the dates empty to run it until you switch it off.
            </p>
            <Table head={["Banner", "Where", "Window", "State", ""]} testId="banner-table"
                empty={!banners.length && <Empty msg="No banners yet. Create one to run a timed promotion." />}>
                {banners.map((b) => (
                    <tr key={b.id} data-testid={`banner-row-${b.id}`}>
                        <Td>
                            <div className="font-semibold">{b.name}</div>
                            <div className="text-xs text-zinc-500 line-clamp-1">{b.message}</div>
                        </Td>
                        <Td className="text-sm text-zinc-400">{PLACEMENT_LABELS[b.placement] || b.placement}</Td>
                        <Td className="text-xs text-zinc-400">
                            <div>{b.start_at ? dt(b.start_at) : "Starts immediately"}</div>
                            <div>{b.end_at ? dt(b.end_at) : "No end date"}</div>
                        </Td>
                        <Td><Badge tone={STATE_TONE[b.state] || "muted"}>{(b.state || "").toUpperCase()}</Badge></Td>
                        <Td>
                            <div className="flex items-center gap-2 justify-end">
                                {canEdit && (
                                    <Btn variant="ghost" data-testid={`banner-edit-${b.id}`}
                                        onClick={() => setEditing({ ...b, start_local: toLocalInput(b.start_at),
                                            end_local: toLocalInput(b.end_at) })}>EDIT</Btn>
                                )}
                                {canEdit && (
                                    <button onClick={() => remove(b)} data-testid={`banner-delete-${b.id}`}
                                        className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                                )}
                            </div>
                        </Td>
                    </tr>
                ))}
            </Table>

            {editing && (
                <BannerDialog form={editing} setForm={setEditing} onSave={save}
                    placements={data?.placements || []} tones={data?.tones || []} />
            )}
        </Card>
    );
};

const BannerDialog = ({ form, setForm, onSave, placements, tones }) => {
    const set = (k, v) => setForm({ ...form, [k]: v });
    return (
        <Modal open onClose={() => setForm(null)} wide testId="banner-dialog"
            title={form.id ? "Edit banner" : "New promo banner"}>
            <div className="grid md:grid-cols-2 gap-4">
                <Field label="Banner name" hint="Internal name, not shown to visitors">
                    <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="banner-name"
                        placeholder="Diwali offer bar" />
                </Field>
                <Field label="Where it appears">
                    <Select value={form.placement} onChange={(e) => set("placement", e.target.value)}
                        data-testid="banner-placement"
                        options={placements.map((p) => ({ value: p, label: PLACEMENT_LABELS[p] || p }))} />
                </Field>
                <Field label="Headline (optional)">
                    <Input value={form.headline} onChange={(e) => set("headline", e.target.value)}
                        data-testid="banner-headline" placeholder="FESTIVE OFFER" />
                </Field>
                <Field label="Style">
                    <Select value={form.tone} onChange={(e) => set("tone", e.target.value)} data-testid="banner-tone"
                        options={tones.map((t) => ({ value: t, label: t }))} />
                </Field>
                <div className="md:col-span-2">
                    <Field label="Message" required>
                        <Textarea rows={2} value={form.message} onChange={(e) => set("message", e.target.value)}
                            data-testid="banner-message" placeholder="Flat 30% off ReDrive Max until Sunday" />
                    </Field>
                </div>
                <Field label="Button label (optional)">
                    <Input value={form.link_label} onChange={(e) => set("link_label", e.target.value)}
                        data-testid="banner-link-label" placeholder="Shop now" />
                </Field>
                <Field label="Button link">
                    <Input value={form.link_href} onChange={(e) => set("link_href", e.target.value)}
                        data-testid="banner-link-href" placeholder="/shop" />
                </Field>
                <Field label="Starts" hint="Leave empty to start immediately">
                    <Input type="datetime-local" value={form.start_local || ""}
                        onChange={(e) => set("start_local", e.target.value)} data-testid="banner-start" />
                </Field>
                <Field label="Ends — hides itself automatically" hint="Leave empty to run until switched off">
                    <Input type="datetime-local" value={form.end_local || ""}
                        onChange={(e) => set("end_local", e.target.value)} data-testid="banner-end" />
                </Field>
                <Field label="Priority" hint="Higher shows first when two banners overlap">
                    <Input type="number" value={form.priority} onChange={(e) => set("priority", e.target.value)}
                        data-testid="banner-priority" />
                </Field>
                <div className="flex items-end gap-6">
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!form.is_active} data-testid="banner-active"
                            onChange={(e) => set("is_active", e.target.checked)} /> Active
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!form.dismissible} data-testid="banner-dismissible"
                            onChange={(e) => set("dismissible", e.target.checked)} /> Visitors can close it
                    </label>
                </div>
            </div>

            <div className="mt-6 border border-white/10 p-4">
                <div className="mono text-[10px] tracking-[0.18em] text-zinc-500 mb-3">PREVIEW</div>
                <div className={`px-4 py-2.5 text-center text-sm ${
                    form.tone === "lime" ? "bg-lime text-bg0" : form.tone === "dark" ? "bg-bg0 border border-white/10"
                        : form.tone === "outline" ? "border border-gold/40 text-gold" : "bg-gold text-bg0"}`}>
                    {form.headline && <strong className="mono text-[10px] tracking-[0.16em] mr-2">{form.headline}</strong>}
                    {form.message || "Your message appears here"}
                    {form.link_label && <span className="underline ml-2 font-bold">{form.link_label}</span>}
                </div>
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
                <div className="mono text-[10px] tracking-[0.16em] text-zinc-500 inline-flex items-center gap-2">
                    <CalendarClock className="w-3.5 h-3.5" /> TIMES USE YOUR DEVICE TIME ZONE
                </div>
                <div className="flex gap-2">
                    <Btn variant="ghost" onClick={() => setForm(null)}>CANCEL</Btn>
                    <Btn onClick={() => onSave(form)} disabled={!form.message} data-testid="banner-save">SAVE BANNER</Btn>
                </div>
            </div>
        </Modal>
    );
};
